/**
 * MyRequestsPage.tsx — Requestor's personal view of their submitted requests.
 *
 * Visible to: all authenticated users (Requestor role).
 *
 * Filtering strategy mirrors DashboardPage: fetches page_size=500 then
 * applies multi-select filters client-side for the same reason (backend
 * query params are single-value only).
 *
 * Key UX features:
 *   - Empty state with CTA when the user has no requests at all
 *   - Action-needed banner when one or more requests are AwaitingInfo;
 *     tapping "Review now" either navigates directly (single request) or
 *     sets the status filter to AwaitingInfo (multiple)
 *   - Stat cards derived from the full allItems list, not separate queries,
 *     because the requestor's total volume is typically small
 *   - "Closed" is intentionally omitted from STATUS_OPTIONS to keep the
 *     filter UI focused on actionable states
 */

import { useMemo, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Filter,
  PlusCircle,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RequestTable } from "@/components/request/RequestTable";
import { useMyRequests } from "@/hooks/useRequests";
import { cn } from "@/lib/utils";
import {
  PRIORITIES,
  REQUEST_TYPES,
  STATUS_LABELS,
  STATUS_ACTIVE_COLORS,
  PRIORITY_COLORS,
  PRIORITY_ACTIVE_COLORS,
  TYPE_COLORS,
  TYPE_ACTIVE_COLORS,
  MY_REQUESTS_REFRESH_INTERVAL_MS as REFRESH_MS,
} from "@/lib/constants";
import type {
  BlinkRequest,
  Priority,
  RequestStatus,
  RequestType,
} from "@/lib/types";

// ── Constants ─────────────────────────────────────────────────────────────────

// "Closed" is omitted — it's a terminal archive state the requestor doesn't need to filter on
const STATUS_OPTIONS: RequestStatus[] = [
  "Submitted",
  "InReview",
  "AwaitingInfo",
  "InfoReceived",
  "Approved",
  "InProgress",
  "Completed",
  "Rejected",
  "Cancelled",
];

// Statuses that count toward the "Pending review" stat card
const PENDING_STATUSES: RequestStatus[] = [
  "Submitted",
  "InReview",
  "AwaitingInfo",
  "InfoReceived",
];

// ── Sub-components ────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
}

/** Single KPI tile. Unlike DashboardPage, value is always a number (derived locally). */
function StatCard({ icon: Icon, label, value, color }: StatCardProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 pt-6 pb-5">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            color,
          )}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold leading-none">{value}</p>
          <p className="text-xs text-muted-foreground mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Immutable toggle — same helper as DashboardPage; duplicated here to keep
 * each page self-contained (no shared utils dependency for a one-liner).
 */
function toggle<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

// ── Page component ────────────────────────────────────────────────────────────

/** Requestor's personal request tracker with inline stat cards and action banners. */
export function MyRequestsPage() {
  // ── Filter state ────────────────────────────────────────────────────────────
  const [statuses, setStatuses] = useState<RequestStatus[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [types, setTypes] = useState<RequestType[]>([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // ── Filter panel state ───────────────────────────────────────────────────────
  type FilterCat = "status" | "priority" | "type";
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<FilterCat>("status");
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const openPanel = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 6, left: rect.left });
    }
    setFilterOpen(true);
  };

  const togglePanel = () => (filterOpen ? setFilterOpen(false) : openPanel());

  const CAT_META: Record<
    FilterCat,
    {
      label: string;
      catLabel: string;
      dot: string;
      line: string;
      badge: string;
      chip: string;
    }
  > = {
    status: {
      label: "Status",
      catLabel: "Request Status",
      dot: "bg-blue-500",
      line: "bg-blue-500",
      badge: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
      chip: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700",
    },
    priority: {
      label: "Priority",
      catLabel: "Priority Level",
      dot: "bg-amber-500",
      line: "bg-amber-500",
      badge: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
      chip: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700",
    },
    type: {
      label: "Type",
      catLabel: "Request Type",
      dot: "bg-violet-500",
      line: "bg-violet-500",
      badge:
        "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
      chip: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-700",
    },
  };

  function getCatCount(cat: FilterCat) {
    return { status: statuses, priority: priorities, type: types }[cat].length;
  }

  function clearFiltersOnly() {
    setStatuses([]);
    setPriorities([]);
    setTypes([]);
  }

  function toggleOption(cat: FilterCat, value: string) {
    switch (cat) {
      case "status":
        setStatuses((prev) => toggle(prev, value as RequestStatus));
        break;
      case "priority":
        setPriorities((prev) => toggle(prev, value as Priority));
        break;
      case "type":
        setTypes((prev) => toggle(prev, value as RequestType));
        break;
    }
  }

  function isOptionSelected(cat: FilterCat, value: string): boolean {
    switch (cat) {
      case "status":
        return statuses.includes(value as RequestStatus);
      case "priority":
        return priorities.includes(value as Priority);
      case "type":
        return types.includes(value as RequestType);
    }
  }

  function getActiveClass(cat: FilterCat, value: string): string {
    switch (cat) {
      case "status":
        return STATUS_ACTIVE_COLORS[value as RequestStatus];
      case "priority":
        return PRIORITY_ACTIVE_COLORS[value as Priority];
      case "type":
        return TYPE_ACTIVE_COLORS[value as RequestType];
    }
  }

  function getOptionLabel(cat: FilterCat, value: string): string {
    if (cat === "status") return STATUS_LABELS[value as RequestStatus];
    return value;
  }

  function getOptions(cat: FilterCat): string[] {
    switch (cat) {
      case "status":
        return STATUS_OPTIONS as unknown as string[];
      case "priority":
        return PRIORITIES as unknown as string[];
      case "type":
        return REQUEST_TYPES as unknown as string[];
    }
  }

  // Chip entries for the active-filter strip
  const chipEntries: Array<{ cat: FilterCat; value: string }> = [
    ...statuses.map((v) => ({ cat: "status" as FilterCat, value: v })),
    ...priorities.map((v) => ({ cat: "priority" as FilterCat, value: v })),
    ...types.map((v) => ({ cat: "type" as FilterCat, value: v })),
  ];

  // ── Data fetching ───────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useMyRequests(
    { page: 1, page_size: 500, search: search || undefined },
    { refetchInterval: REFRESH_MS },
  );

  const allItems: BlinkRequest[] = data?.items ?? [];

  // ── Derived stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const counts = {
      total: allItems.length,
      pending: 0,
      awaitingInfo: 0,
      approved: 0,
      rejected: 0,
    };
    for (const r of allItems) {
      if (PENDING_STATUSES.includes(r.status)) counts.pending++;
      if (r.status === "AwaitingInfo") counts.awaitingInfo++;
      if (["Approved", "InProgress", "Completed"].includes(r.status))
        counts.approved++;
      if (r.status === "Rejected") counts.rejected++;
    }
    return counts;
  }, [allItems]);

  // ── Client-side filtering ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let items = allItems;
    if (statuses.length > 0)
      items = items.filter((r) => statuses.includes(r.status));
    if (priorities.length > 0)
      items = items.filter((r) => priorities.includes(r.priority));
    if (types.length > 0)
      items = items.filter((r) => types.includes(r.request_type));
    return items;
  }, [allItems, statuses, priorities, types]);

  const filterCount = statuses.length + priorities.length + types.length;
  const activeCount = filterCount + (search ? 1 : 0);
  const hasFilters = activeCount > 0;

  const clearAll = () => {
    setStatuses([]);
    setPriorities([]);
    setTypes([]);
    setSearch("");
  };

  if (!isLoading && !isError && allItems.length === 0 && !search) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold mb-1">No requests yet</h2>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          You haven't submitted any tech requests. Start one whenever you have a
          feature idea or have spotted a defect.
        </p>
        <Button asChild>
          <Link to="/submit">
            <PlusCircle className="mr-2 h-4 w-4" />
            Submit your first request
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track the status of every request you've submitted to the Blink
            product teams.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs h-8"
              onClick={clearAll}
            >
              <X className="h-3 w-3" />
              Clear {activeCount} filter{activeCount !== 1 ? "s" : ""}
            </Button>
          )}
          <Button asChild>
            <Link to="/submit">
              <PlusCircle className="mr-2 h-4 w-4" />
              New Request
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={FileText}
          label="Total submitted"
          value={stats.total}
          color="bg-slate-600"
        />
        <StatCard
          icon={Clock}
          label="Pending review"
          value={stats.pending}
          color="bg-amber-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Approved"
          value={stats.approved}
          color="bg-green-600"
        />
        <StatCard
          icon={X}
          label="Rejected"
          value={stats.rejected}
          color="bg-red-500"
        />
      </div>

      {/* Action-needed banner */}
      {stats.awaitingInfo > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {stats.awaitingInfo === 1
                  ? "1 request needs"
                  : `${stats.awaitingInfo} requests need`}{" "}
                additional information from you.
              </p>
              <p className="text-xs text-amber-800">
                Reviewers have asked clarifying questions — please respond so
                they can move forward.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const awaitingItems = allItems.filter(
                  (r) => r.status === "AwaitingInfo",
                );
                if (awaitingItems.length === 1) {
                  navigate(`/respond/${awaitingItems[0].id}`);
                } else {
                  setStatuses(["AwaitingInfo"]);
                }
              }}
              className="border-amber-300 bg-white dark:bg-gray-900 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-950"
            >
              Review now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Search + filter bar */}
      <div className="space-y-2">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search requests by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 h-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter trigger + active chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            ref={triggerRef}
            type="button"
            onClick={togglePanel}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-all shrink-0",
              filterOpen
                ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/10"
                : "border-border bg-card text-muted-foreground hover:border-muted-foreground hover:text-foreground",
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {filterCount > 0 && (
              <span className="ml-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                {filterCount}
              </span>
            )}
          </button>

          {/* Active filter chips */}
          {chipEntries.map(({ cat, value }) => {
            const meta = CAT_META[cat];
            return (
              <div
                key={`${cat}-${value}`}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium",
                  meta.chip,
                )}
              >
                <span className="text-[9px] uppercase tracking-wider opacity-60">
                  {meta.label}
                </span>
                <span className="mx-0.5">{getOptionLabel(cat, value)}</span>
                <button
                  type="button"
                  onClick={() => toggleOption(cat, value)}
                  className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full opacity-50 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/20 transition-opacity"
                  aria-label={`Remove ${getOptionLabel(cat, value)}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            );
          })}

          {filterCount > 0 && (
            <button
              type="button"
              onClick={clearFiltersOnly}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          {hasFilters && !isLoading && (
            <p className="text-xs text-muted-foreground mb-3">
              {filtered.length} of {allItems.length} requests match active
              filters
            </p>
          )}
          <RequestTable
            requests={filtered}
            isLoading={isLoading}
            hideSubmitter
            rowLinkBuilder={(r) =>
              r.status === "AwaitingInfo"
                ? `/respond/${r.id}`
                : `/requests/${r.id}`
            }
          />
          {isError && (
            <p className="text-sm text-destructive mt-2">
              Couldn't load your requests. Please refresh and try again.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Floating two-panel filter card */}
      {filterOpen && (
        <div
          ref={panelRef}
          className="fixed z-50 w-[460px] max-w-[calc(100vw-32px)] rounded-xl border bg-card shadow-xl"
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          <div className="flex min-h-[200px]">
            {/* Left: category list */}
            <div className="w-36 shrink-0 border-r bg-muted/40 py-2">
              {(["status", "priority", "type"] as FilterCat[]).map((cat) => {
                const meta = CAT_META[cat];
                const count = getCatCount(cat);
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      "relative flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium transition-colors text-left",
                      isActive
                        ? "bg-card text-foreground"
                        : "text-muted-foreground hover:bg-card/60 hover:text-foreground",
                    )}
                  >
                    {isActive && (
                      <span
                        className={cn(
                          "absolute left-0 top-2 bottom-2 w-0.5 rounded-r",
                          meta.line,
                        )}
                      />
                    )}
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        meta.dot,
                        isActive ? "opacity-100" : "opacity-35",
                      )}
                    />
                    {meta.label}
                    {count > 0 && (
                      <span
                        className={cn(
                          "ml-auto rounded-full px-1.5 py-px text-[10px] font-bold",
                          meta.badge,
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right: option grid */}
            <div className="flex-1 p-4">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {CAT_META[activeCategory].catLabel}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {getOptions(activeCategory).map((value) => {
                  const selected = isOptionSelected(activeCategory, value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleOption(activeCategory, value)}
                      aria-pressed={selected}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs font-medium transition-all",
                        selected
                          ? getActiveClass(activeCategory, value)
                          : "border-border bg-background text-foreground hover:border-muted-foreground hover:bg-muted/50",
                      )}
                    >
                      {getOptionLabel(activeCategory, value)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t px-4 py-2.5">
            <button
              type="button"
              onClick={clearFiltersOnly}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear all
            </button>
            <span className="text-[11px] text-muted-foreground/50">
              Filters apply instantly
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
